import _ from 'lodash';
import {
    ProviderInstitutionalClaimTemplate,
    ProviderProfessionalClaimTemplate,
    SupportInstitutionalClaimTemplate,
    SupportProfessionalClaimTemplate,
} from '../../../api';
import { PaymentItemKeyInput } from '../matching-keys';

type ClaimTemplate =
    | ProviderProfessionalClaimTemplate
    | ProviderInstitutionalClaimTemplate
    | SupportProfessionalClaimTemplate
    | SupportInstitutionalClaimTemplate;

export interface IMismatchDescription {
    full: string;
    short: string;
    isNoLikelyMatch: boolean;
}

export interface IMismatchGroupMeta {
    contextLabel?: string;
    receivedTotal: number;
}

export interface IMismatchGroupPayment {
    id?: string;
    receivedDate?: string;
    paymentDate?: string;
}

export interface IMismatchGroupRecord {
    paid?: number;
    items?: Array<{ paymentId?: string }>;
}

const normalizeProcedureCode = (value?: string): string | undefined => {
    const trimmed = value ? _.trim(value) : undefined;

    return trimmed ? _.toUpper(trimmed) : undefined;
};

const normalizeDos = (value?: string): string | undefined => {
    const trimmed = value ? _.trim(value) : undefined;

    return trimmed || undefined;
};

export const findLikelyMatchLineNumber = (
    template: ClaimTemplate | undefined,
    item: PaymentItemKeyInput,
): number | undefined => {
    if (!template) {
        return undefined;
    }

    const itemProc = normalizeProcedureCode(item.procedureCode);
    const itemDos = normalizeDos(item.fromDos);

    if (!itemProc) {
        return undefined;
    }

    if ('f24ServiceLine' in template) {
        const lines = template.f24ServiceLine || [];
        const matchIndex = lines.findIndex(line => {
            const lineProc = normalizeProcedureCode(line.f24dProcedureCode);
            const lineDos = normalizeDos(line.f24aFromDateOfService);

            if (lineProc !== itemProc) {
                return false;
            }

            return !itemDos || !lineDos || lineDos === itemDos;
        });

        return matchIndex >= 0 ? matchIndex + 1 : undefined;
    }

    if ('f42ClaimLines' in template) {
        const lines = template.f42ClaimLines || [];
        const matchIndex = lines.findIndex(line => {
            const lineProc = normalizeProcedureCode(line.f44ProcedureCode);
            const lineDos = normalizeDos(line.f45FromServiceDate);

            if (lineProc !== itemProc) {
                return false;
            }

            return !itemDos || !lineDos || lineDos === itemDos;
        });

        return matchIndex >= 0 ? matchIndex + 1 : undefined;
    }

    return undefined;
};

export const buildMismatchDescription = (
    item: PaymentItemKeyInput & { controlNumber?: string; x12ControlNumber?: string; throughDos?: string },
    lineNumber: number | undefined,
    formatDos: (from?: string, through?: string) => string | undefined,
): IMismatchDescription => {
    const detailParts: string[] = [];

    if (item.procedureCode) {
        detailParts.push(`CPT ${item.procedureCode}`);
    }

    const dos = formatDos(item.fromDos, item.throughDos);
    if (dos) {
        detailParts.push(`DOS ${dos}`);
    }

    const controlNumber = item.x12ControlNumber || item.controlNumber;
    if (controlNumber) {
        detailParts.push(`control #${controlNumber}`);
    }

    if (!lineNumber) {
        return {
            full: 'No likely match',
            short: 'No likely match',
            isNoLikelyMatch: true,
        };
    }

    const detail = detailParts.join(', ');
    const full = detail ? `Line ${lineNumber} · ${detail} differs.` : `Line ${lineNumber} differs.`;
    const short = item.procedureCode ? `Line ${lineNumber} · CPT ${item.procedureCode}` : `Line ${lineNumber}`;

    return {
        full,
        short: full.length > short.length ? short : full,
        isNoLikelyMatch: false,
    };
};

export const buildMismatchGroupMeta = (
    mismatchedRecords: IMismatchGroupRecord[],
    payments: IMismatchGroupPayment[],
    payerTitle?: string,
    formatReceivedDate?: (date: string) => string,
): IMismatchGroupMeta => {
    const paymentIds = new Set(
        _.compact(_.flatMap(mismatchedRecords, record => (record.items || []).map(item => item.paymentId))),
    );

    const relatedPayments = payments.filter(payment => payment.id && paymentIds.has(payment.id));
    const receivedDatePayment = _.maxBy(
        relatedPayments,
        payment => payment.receivedDate || payment.paymentDate || '',
    );
    const receivedDate = receivedDatePayment?.receivedDate || receivedDatePayment?.paymentDate;

    const contextParts = _.compact([
        payerTitle,
        receivedDate ? `ERA received ${formatReceivedDate ? formatReceivedDate(receivedDate) : receivedDate}` : undefined,
    ]);

    const receivedTotal = mismatchedRecords.reduce((total, record) => total + (record.paid || 0), 0);

    return {
        contextLabel: contextParts.length ? contextParts.join(' · ') : undefined,
        receivedTotal,
    };
};
